import { ChevronRight, Home } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Breadcrumbs Component
 * Shows current page hierarchy and navigation path
 * Generates breadcrumbs based on current route
 * Accessible with ARIA navigation landmark
 */

const routeLabels = {
  dashboard: 'Dashboard',
  orders: 'Orders',
  users: 'Users',
  drivers: 'Drivers',
  agents: 'Agents',
  transactions: 'Wallet / Transactions',
  reports: 'Reports',
  'delete-requests': 'Delete Requests',
  'blocked-numbers': 'Blocked Numbers',
  notifications: 'Notifications',
  settings: 'Settings',
  'audit-logs': 'Audit Logs',
  profile: 'Profile',
  details: 'Details',
};

export default function Breadcrumbs() {
  const location = useLocation();
  
  // Parse pathname to breadcrumb items
  const pathSegments = location.pathname.split('/').filter(Boolean);
  
  if (pathSegments.length === 0 || pathSegments[0] === 'dashboard') {
    return null; // Don't show breadcrumbs on dashboard
  }

  const breadcrumbs = pathSegments.map((segment, index) => {
    const path = '/' + pathSegments.slice(0, index + 1).join('/');
    const label = routeLabels[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    const isLast = index === pathSegments.length - 1;

    return {
      path,
      label,
      isLast,
    };
  });

  return (
    <nav
      aria-label="Breadcrumb"
      className="bg-white/60 backdrop-blur-lg border-b border-slate-200/60 px-4 md:px-6 lg:px-8 py-3"
    >
      <ol className="flex items-center gap-2 text-sm">
        <li>
          <Link
            to="/dashboard"
            className="flex items-center text-slate-600 hover:text-primary-600 transition-colors p-1 hover:bg-slate-100 rounded-lg"
            aria-label="Home"
          >
            <Home className="w-4 h-4" aria-hidden="true" />
          </Link>
        </li>

        {breadcrumbs.map((crumb, index) => (
          <li key={crumb.path} className="flex items-center gap-2">
            <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />
            {crumb.isLast ? (
              <span
                className="font-medium text-slate-900"
                aria-current="page"
              >
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.path}
                className="text-slate-600 hover:text-primary-600 transition-colors px-2 py-1 hover:bg-slate-100 rounded-lg"
              >
                {crumb.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}
